import { networkTypeSchema } from './network';
import { z } from 'zod';

// We keep these as type-only imports (no runtime dependency on `@mls-wallet/database`),
// while still validating the runtime values are shaped like IDs.
const userIdSchema = z.custom(
  (value) => z.string().min(1).safeParse(value).success,
  { message: 'Invalid UserId' }
);
export type UserId = z.infer<typeof userIdSchema>;
const organizationIdSchema = z.custom(
  (value) => z.uuidv4().safeParse(value).success,
  { message: 'Invalid OrganizationId' }
);
export type OrganizationId = z.infer<typeof organizationIdSchema>;

const httpRequestSchema = z.object({
  id: z.string().optional(),
  method: z.string().optional(),
  url: z.string().optional(),
  user_agent: z.string().optional(),
  /** Optional header for Den app version - currently only used for mobile app requests */
  app_version: z.string().optional(),
  /** Native build number (Expo Application.nativeBuildVersion), header X-Build-Number */
  build_number: z.string().optional(),
  /** Optional header for app OS platform - currently only used for mobile app requests */
  platform_os: z.string().optional(),
  timestamp: z.string().optional(),
  signature: z.string().optional(),
  body: z.record(z.string(), z.any()).optional(),
});

const httpResponseSchema = z.object({
  status: z.number().optional(),
  content_length: z.number().optional(),
  /** milliseconds */
  response_time: z.number().optional(),
});

const userSchema = z.object({
  id: userIdSchema.optional(),
});

const orgSchema = z.object({
  id: organizationIdSchema.optional(),
});

const transactionDataSchema = z.object({
  is_executed: z.boolean().optional(),
  data: z.string().optional(),
  to: z.string().optional(),
  safe_tx_hash: z.string().optional(),
  nonce: z.number().optional(),
  /** String to preserve precision */
  value: z.string().optional(),
});

const contractDataSchema = z.object({
  network_id: z.number().optional(),
  address: z.string().optional(),
});

export const allowedLogFieldsSchema = z.object({
  http_request: httpRequestSchema.optional(),
  http_response: httpResponseSchema.optional(),
  error: z.union([z.string(), z.instanceof(Error)]).optional(),
  error_code: z.string().optional(),
  user: userSchema.optional(),
  org: orgSchema.optional(),
  tx: transactionDataSchema.optional(),
  contract: contractDataSchema.optional(),
  network_id: networkTypeSchema.optional(),
  // Optional override for the log source (defaults to SERVICE_NAME from logger config)
  // Useful when forwarding logs from another origin (e.g., mobile app logs via sync-server)
  source_override: z.string().optional(),
  // `arbitrary_value` is a catch-all for cases where we don't have a field.
  // We should minimize these / add fields where they may be consistenly used.
  arbitrary_value: z.any().optional(),
});

export type AllowedLogFields = z.infer<typeof allowedLogFieldsSchema>;
